import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { BeneficiaryFormSchema } from '../../schemas/beneficiarySchema';
import { beneficiarySchema } from '../../schemas/beneficiarySchema';
import { useState } from 'react';
import type { ReactElement, SetStateAction } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import * as XLSX from 'xlsx';

export default function FormBeneficiary(): ReactElement {
  const [selecionado, setSelecionado] = useState('');
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<any[][]>([]);

  const handleChange = (valor: SetStateAction<string>) => {
    setSelecionado(valor === selecionado ? '' : valor);
  };

  const {
    register,
    handleSubmit,
    trigger,
    getValues,
    formState: { errors },
  } = useForm<BeneficiaryFormSchema>({
    resolver: zodResolver(beneficiarySchema),
  });

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const validExtensions = ['.xlsx', '.xls'];
      const maxSizeMB = 5;
      const fileExtension = file.name.substring(file.name.lastIndexOf('.'));

      if (!validExtensions.includes(fileExtension)) {
        toast.error('Formato inválido. Envie um arquivo .xls ou .xlsx.');
        return;
      }

      if (file.size > maxSizeMB * 1024 * 1024) {
        toast.error(`Arquivo muito grande. Máximo permitido: ${maxSizeMB} MB.`);
        return;
      }

      setExcelFile(file);
      toast.success('Arquivo carregado com sucesso!');

      const reader = new FileReader();
      reader.onload = event => {
        const data = new Uint8Array(event.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData: any[][] = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
        });

        setPreviewData(jsonData.slice(0, 30));
      };
      reader.readAsArrayBuffer(file);
    }
  }

  async function onSubmit(data: BeneficiaryFormSchema) {
    const formData = new FormData();
    formData.append('formData', JSON.stringify(data));
    if (excelFile) formData.append('file', excelFile);

    const toastId = toast.loading('Enviando dados...');

    try {
      const response = await fetch(
        'https://dev.apievo.odontogroup.com.br/include/excel',
        {
          method: 'POST',
          headers: {
            Authorization:
              'Bearer b7f9a42f0ef97b779077b73214a6cff24bbfd8d5b414eb64155b9594bc96d30b',
          },
          body: formData,
          credentials: 'include',
        },
      );

      toast.dismiss(toastId);

      if (!response.ok) {
        const err = await response.text();
        throw new Error('Erro no upload: ' + err);
      }

      toast.success('Upload e envio feitos com sucesso!');
    } catch (error) {
      toast.dismiss(toastId);
      toast.error('Erro: ' + error);
    }
  }

  const handleClickEnviar = async () => {
    const isValid = await trigger();

    if (!selecionado) {
      toast.error(
        'Por favor, selecione uma opção (Empresarial ou Administradora).',
      );
      return;
    }

    if (!excelFile) {
      toast.error('Por favor, envie uma planilha Excel.');
      return;
    }

    const data = getValues();
    onSubmit(data);
  };

  return (
    <>
      <Toaster position="top-center" reverseOrder={false} />
      <div className="mb-5 p-5">
        <div className="p-10">
          <form
            onSubmit={e => e.preventDefault()} // impede envio padrão
            className="max-w-3xl mx-auto p-8 mb-10 mt-10 rounded-xl shadow-lg grid gap-8"
          >
            <h2 className="text-3xl font-extrabold text-blue-900 text-center"></h2>

            <div className="w-50 relative flex gap-2">
              <label
                htmlFor="checkbox1"
                className="relative h-6 w-6 flex items-center justify-center overflow-hidden rounded-full bg-gradient-to-tr from-[#4158D0] via-[#131446] to-[#ffcd7065] p-1 duration-100 hover:p-1.5 cursor-pointer"
              >
                <input
                  type="checkbox"
                  className="peer hidden"
                  id="checkbox1"
                  checked={selecionado === 'empressarial'}
                  onChange={() => handleChange('empressarial')}
                />
                <div className="absolute left-[0.47rem] h-[3px] w-[14px] -translate-y-8 translate-x-8 rotate-[-71deg] rounded-sm bg-white duration-300 peer-checked:translate-x-0 peer-checked:translate-y-0"></div>
                <div className="absolute left-1 top-[0.87rem] h-[3px] w-[10px] -translate-x-8 -translate-y-8 rotate-[25deg] rounded-sm bg-white duration-300 peer-checked:translate-x-0 peer-checked:translate-y-0"></div>
              </label>
              <span>Empressarial</span>
            </div>

            <div className="w-50 relative flex gap-2">
              <label
                htmlFor="checkbox2"
                className="relative h-6 w-6 flex items-center justify-center overflow-hidden rounded-full bg-gradient-to-tr from-[#4158D0] via-[#131446] to-[#ffcd7065] p-1 duration-100 hover:p-1.5 cursor-pointer"
              >
                <input
                  type="checkbox"
                  className="peer hidden"
                  id="checkbox2"
                  checked={selecionado === 'administradora'}
                  onChange={() => handleChange('administradora')}
                />
                <div className="absolute left-[0.47rem] h-[3px] w-[14px] -translate-y-8 translate-x-8 rotate-[-71deg] rounded-sm bg-white duration-300 peer-checked:translate-x-0 peer-checked:translate-y-0"></div>
                <div className="absolute left-1 top-[0.87rem] h-[3px] w-[10px] -translate-x-8 -translate-y-8 rotate-[25deg] rounded-sm bg-white duration-300 peer-checked:translate-x-0 peer-checked:translate-y-0"></div>
              </label>
              <span>Administradora</span>
            </div>

            <div className="flex flex-col">
              <label
                htmlFor="excelFile"
                className="mb-2 font-semibold text-blue-800"
              >
                Upload da Planilha Excel
              </label>
              <input
                id="excelFile"
                type="file"
                accept=".xlsx, .xls"
                onChange={handleFileChange}
                className="w-full px-4 py-2 border border-blue-300 rounded-lg bg-blue-50 text-blue-900 font-medium focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-600 transition"
              />
              {excelFile && (
                <p className="text-sm text-red-700 mt-2">
                  Arquivo selecionado: <strong>{excelFile.name}</strong>
                </p>
              )}
            </div>

            {previewData.length > 0 && (
              <div className="mt-4 overflow-auto border w-full rounded-lg max-h-64">
                <table className="w-full text-sm text-left text-blue-800 border-collapse">
                  <thead className="bg-blue-100 sticky top-0 z-10">
                    <tr>
                      {previewData[0].map((col, index) => (
                        <th
                          key={index}
                          className="px-3 py-2 border border-blue-300 font-bold"
                        >
                          {col || `Coluna ${index + 1}`}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.slice(1).map((row, rowIndex) => (
                      <tr key={rowIndex} className="hover:bg-blue-50">
                        {row.map((cell, colIndex) => (
                          <td
                            key={colIndex}
                            className="px-3 py-2 border border-blue-200"
                          >
                            {cell ?? '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex gap-4">
              <button
                type="button"
                onClick={handleClickEnviar}
                className="w-1/2 py-4 bg-gradient-to-r from-blue-600 to-blue-900 text-white font-extrabold rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-800 transition"
              >
                Enviar
              </button>
              <button
                type="button"
                onClick={() => (window.location.href = '/')}
                className="w-1/2 py-4 bg-gradient-to-r from-blue-600 to-blue-900 text-white font-extrabold rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-800 transition"
              >
                Sair
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
